/**
 * 올인원 — 주렁은행 탭 (옵션 A: 인라인 통합)
 * docs/tools_all.md §9 (v9 — 풀 기능 포팅)
 *
 * 환전/저축/기부/내역을 AllInOne 컨텍스트에 인라인 통합. (구 bank 서비스 port 59015 는 은퇴 — 본 탭으로 대체)
 * iframe/별창 미사용 — useMemberIdentity 가 이미 가족 식별을 마쳤으므로 api-server /coin/* 직접 호출(services/api.js).
 *
 * 구성:
 *  - 상단 가족 선택 및 잔액 요약
 *  - 업무 선택 / 수량 키패드 / 거래 확인을 같은 위치에서 사용
 *  - 거래 후 markDirty('bank-coin') + refresh + appendLog
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Space, Typography, Empty, Drawer, Spin, Alert,
  Modal, App as AntApp, Table,
} from 'antd';
import {
  HeartOutlined, HistoryOutlined,
  ArrowDownOutlined, ArrowUpOutlined, ReloadOutlined, CheckOutlined,
} from '@ant-design/icons';
import {
  exchangeCoins, depositCoins, donateCoins,
  getTransactionHistory, getCoinBalance,
} from '../../services/api';
import bankCoin from '../../assets/service-menu/bank.png';
import withdrawCharacter from '../../assets/service-menu/bank-withdraw-capybara-v2.png';
import depositCharacter from '../../assets/service-menu/bank-deposit-toucan.png';
import donateCharacter from '../../assets/service-menu/bank-donate-fox.png';
import { useAuth } from '../../contexts/AuthContext';

const { Text } = Typography;

const MAX_DONATE = 100;

const getDisplayName = (m) =>
  m?.nickname || m?.name || (m?.member_type === 'CHILD' ? '자녀' : '회원');

export const transactionLabel = (type, reason) => {
  if (type === 'EARN') {
    switch (reason) {
      case 'SIGNUP': return { label: '가입 보너스', icon: '🌱', cls: 'earn' };
      case 'VISIT': return { label: '입장 적립', icon: '🎟️', cls: 'earn' };
      case 'INFO_INPUT': return { label: '정보 입력 보너스', icon: '📝', cls: 'earn' };
      case 'REVISIT_BONUS': return { label: '재방문 보너스', icon: '⭐', cls: 'earn' };
      case 'PASSPORT_PURCHASE': return { label: '여권 구매 보너스', icon: '📘', cls: 'passport' };
      case 'PASSPORT_ENTRY_BONUS': return { label: '여권 입장 보너스', icon: '🛂', cls: 'passport' };
      case 'PASSPORT_REVISIT_BONUS': return { label: '여권 재방문 보너스', icon: '🛂', cls: 'passport' };
      case 'PASSPORT_BIRTHDAY_BONUS': return { label: '여권 생일 보너스', icon: '🎂', cls: 'passport' };
      // 저축 이자는 2026-08-26 부터 원금과 **별도 행**으로 기록된다(기획서 §4.13).
      // 그 이전 저축분은 원금 행에 합산돼 있어 이 라벨로 나타나지 않는다.
      case 'PASSPORT_DEPOSIT_INTEREST': return { label: '여권 저축 이자', icon: '🐷', cls: 'passport' };
      case 'MANUAL': return { label: '운영자 수동 지급', icon: '🎉', cls: 'event' };
      default: return { label: `적립 (${reason || '기타'})`, icon: '🪙', cls: 'earn' };
    }
  }
  switch (type) {
    case 'EXCHANGE_OUT': return { label: '인출 (디지털→실물)', icon: '🏧', cls: 'withdraw' };
    case 'DEPOSIT': return { label: '저축 (실물→디지털)', icon: '🐷', cls: 'deposit' };
    case 'DONATE': return { label: '기부 (디지털 차감)', icon: '💝', cls: 'donate' };
    case 'FAMILY_TRANSFER_OUT': return { label: '가족 전달 (보냄)', icon: '👨‍👩‍👧', cls: 'withdraw' };
    case 'FAMILY_TRANSFER_IN': return { label: '가족 전달 (받음)', icon: '👨‍👩‍👧', cls: 'deposit' };
    case 'WELCOME_CANCEL': return { label: '입장 취소 (회수)', icon: '↩️', cls: 'withdraw' };
    case 'DEDUCT': return { label: '운영자 수동 회수', icon: '↩️', cls: 'withdraw' };
    default: return { label: type, icon: '🪙', cls: 'default' };
  }
};

const formatTime = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return s;
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${m}/${dd} ${h}:${mi}`;
};

export default function BankTab({
  scannedMember, primary, family = [], passportMap = {},
  refresh, markDirty, beginAction, endAction, onLog, scanPaused = false,
  phoneSearchAction, nextMemberAction,
}) {
  const { modal, message: messageApi } = AntApp.useApp();
  const { user } = useAuth();

  const targets = (family && family.length) ? family : (scannedMember ? [scannedMember] : []);
  const defaultTargetId =
    primary?.member_id || scannedMember?.member_id || targets[0]?.member_id || null;

  const [activeMemberId, setActiveMemberId] = useState(defaultTargetId);
  const [activeTab, setActiveTab] = useState('exchange');
  const [operationOpen, setOperationOpen] = useState(false);
  const withdrawalConfirmOpen = useRef(false);

  // 거래별 입력 상태
  const [exchangeAmount, setExchangeAmount] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);
  const [donateAmount, setDonateAmount] = useState(0);

  // 처리/오류
  const [processing, setProcessing] = useState(false);
  const [opError, setOpError] = useState(null);
  const [resultPopup, setResultPopup] = useState(null); // { type, amount, balance, interest? }

  // 거래내역
  const [historyDrawer, setHistoryDrawer] = useState({ open: false, loading: false, txs: [], error: null });

  // 잔액 즉시 갱신용 (refresh 가 가족 전체 재조회라 늦을 수 있음)
  const [balanceOverride, setBalanceOverride] = useState({}); // memberId -> balance

  const storeId = localStorage.getItem('store_id') || '3';
  const operatorId = user?.username || user?.user_id || localStorage.getItem('operator_id') || 'tools-operator';

  // family 가 갱신되면 override 비우고 fresh balance 사용
  useEffect(() => {
    setBalanceOverride({});
  }, [family]);

  // 회원이 바뀌면 활성 회원도 재계산
  useEffect(() => {
    if (!targets.length) { setActiveMemberId(null); return; }
    if (!targets.some((m) => m.member_id === activeMemberId)) {
      setActiveMemberId(defaultTargetId);
    }
  }, [targets, activeMemberId, defaultTargetId]);

  const activeMember = useMemo(
    () => targets.find((m) => m.member_id === activeMemberId) || null,
    [targets, activeMemberId],
  );

  const activeBalance = useMemo(() => {
    if (!activeMember) return 0;
    if (balanceOverride[activeMember.member_id] != null) return balanceOverride[activeMember.member_id];
    return activeMember.balance ?? activeMember.coin_balance ?? 0;
  }, [activeMember, balanceOverride]);

  const activePassport = activeMember ? passportMap?.[activeMember.member_id] : null;
  const hasPassport = Boolean(activePassport);

  // ─── 입력 리셋 ───
  const resetInputs = useCallback(() => {
    setExchangeAmount(0);
    setDepositAmount(0);
    setDonateAmount(0);
    setOpError(null);
  }, []);

  useEffect(() => { resetInputs(); setOperationOpen(false); }, [activeMemberId, resetInputs]);

  // ─── 공용 거래 실행 ───
  const runOperation = useCallback(async (kind, amount) => {
    if (!activeMember) return;
    if (amount <= 0) { setOpError('수량을 입력해주세요'); return; }
    if (kind === 'exchange' && amount > activeBalance) {
      setOpError('보유 주화보다 많이 인출할 수 없습니다');
      return;
    }
    if (kind === 'donate' && amount > MAX_DONATE) {
      setOpError(`기부는 최대 ${MAX_DONATE}개까지 가능합니다`);
      return;
    }
    const actionId = `bank-${kind}-${activeMember.member_id}-${Date.now()}`;
    setProcessing(true);
    setOpError(null);
    beginAction(actionId);
    try {
      const params = { memberId: activeMember.member_id, amount, storeId, operatorId };
      const apiFn = kind === 'exchange' ? exchangeCoins
        : kind === 'deposit' ? depositCoins
          : donateCoins;
      const res = await apiFn(params);
      if (!res?.success) {
        throw new Error(res?.message || '처리 실패');
      }
      const newBalance = res.balance_after ?? res.balance ?? null;
      if (newBalance != null) {
        setBalanceOverride((prev) => ({ ...prev, [activeMember.member_id]: newBalance }));
      } else {
        try {
          const b = await getCoinBalance(activeMember.member_id);
          if (b?.balance != null) setBalanceOverride((prev) => ({ ...prev, [activeMember.member_id]: b.balance }));
        } catch { /* noop */ }
      }
      const ownerName = getDisplayName(activeMember);
      const verb = kind === 'exchange' ? '인출' : kind === 'deposit' ? '저축' : '기부';
      onLog?.(`✅ [${ownerName}] ${verb} ${amount}개 (잔액 ${newBalance ?? '?'})`);
      setOperationOpen(false);
      setResultPopup({
        type: kind,
        amount,
        balance: newBalance,
        interest: res.interest_amount || 0,
        ownerName,
      });
      messageApi.success(`${ownerName}님 ${verb} ${amount}개 완료`);
      resetInputs();
      markDirty('bank-coin');
      refresh?.();
    } catch (err) {
      const msg = err?.message || '처리 중 오류가 발생했습니다';
      setOpError(msg);
      messageApi.error(msg);
      onLog?.(`❌ ${getDisplayName(activeMember)} ${kind} 실패: ${msg}`);
    } finally {
      endAction(actionId);
      setProcessing(false);
    }
  }, [
    activeMember, activeBalance, storeId, operatorId,
    beginAction, endAction, markDirty, refresh, onLog, messageApi, resetInputs,
  ]);

  // ─── 거래내역 Drawer ───
  const openHistory = async () => {
    if (!activeMember) return;
    setHistoryDrawer({ open: true, loading: true, txs: [], error: null });
    try {
      const res = await getTransactionHistory(activeMember.member_id, 1, 50);
      if (res?.success) {
        setHistoryDrawer({ open: true, loading: false, txs: res.transactions || [], error: null });
      } else {
        setHistoryDrawer({ open: true, loading: false, txs: [], error: res?.message || '내역을 불러올 수 없습니다' });
      }
    } catch (err) {
      setHistoryDrawer({ open: true, loading: false, txs: [], error: err?.message || '내역을 불러올 수 없습니다' });
    }
  };

  if (!scannedMember) return (
    <div className="bank-waiting">
      <div className="bank-waiting-notice">
        <img src={bankCoin} alt="" />
        <div className="bank-waiting-copy">
          <h2><span className="bank-scan-signal" data-paused={scanPaused} aria-hidden="true"><i /></span>회원 QR을 스캔해주세요</h2>
          <p>QR을 스캔하면 정보가 표시됩니다</p>
          <div className="bank-waiting-search">{phoneSearchAction}</div>
        </div>
      </div>
    </div>
  );
  if (!activeMember) return <Empty description="회원 정보가 없습니다" />;

  const operations = [
    { key: 'exchange', title: '인출', detail: '디지털 주화를 실물로', icon: <ArrowDownOutlined /> },
    { key: 'deposit', title: '저축', detail: '실물 주화를 계좌로', icon: <ArrowUpOutlined /> },
    { key: 'donate', title: '기부', detail: '실물 주화를 기부', icon: <HeartOutlined /> },
  ];
  const operation = operations.find(item => item.key === activeTab);
  const amount = activeTab === 'exchange' ? exchangeAmount : activeTab === 'deposit' ? depositAmount : donateAmount;
  const setAmount = activeTab === 'exchange' ? setExchangeAmount : activeTab === 'deposit' ? setDepositAmount : setDonateAmount;
  const limit = activeTab === 'exchange' ? Math.min(activeBalance, 99999) : activeTab === 'donate' ? MAX_DONATE : 99999;
  const enterQuantity = key => {
    if (processing) return;
    setOpError(null);
    setAmount(previous => {
      if (key === 'clear') return 0;
      if (key === 'backspace') return Math.floor(previous / 10);
      return Math.min(Number(String(previous) + key), limit);
    });
  };
  const submitQuantity = () => {
    if (processing || amount <= 0) return;
    if (withdrawalConfirmOpen.current || (activeTab === 'exchange' && amount > activeBalance)) return;
    {
      withdrawalConfirmOpen.current = true;
      modal.confirm({
        title: `${operation.title}하시겠습니까?`,
        className: 'zb-withdraw-confirm',
        centered: true,
        width: 760,
        content: <dl className="zb-confirm-details">
          <div><dt>{operation.title} 대상</dt><dd>{getDisplayName(activeMember)}</dd></div>
          <div><dt>{operation.title} 수량</dt><dd>{amount.toLocaleString()}개</dd></div>
          <div><dt>{operation.title} 후 잔액</dt><dd>{(activeTab === 'exchange' ? activeBalance - amount : activeTab === 'deposit' ? activeBalance + amount : activeBalance).toLocaleString()}개</dd></div>
          {activeTab === 'deposit' && <p className="zb-confirm-note">이자가 발생하면 처리 후 잔액에 별도로 반영됩니다.</p>}
          {activeTab === 'donate' && <p className="zb-confirm-note">실물 주화를 기부하며 디지털 잔액은 차감되지 않습니다.</p>}
        </dl>,
        okText: `${operation.title}하기`, cancelText: '돌아가기',
        maskClosable: false,
        onCancel: () => { withdrawalConfirmOpen.current = false; },
        onOk: async () => {
          try { await runOperation(activeTab, amount); }
          finally { withdrawalConfirmOpen.current = false; }
        },
      });
      return;
    }
  };

  // ─── 거래 내역 컬럼 ───
  const historyColumns = [
    { title: '일시', dataIndex: 'created_at', width: 110, render: (v) => formatTime(v) },
    {
      title: '구분',
      render: (_, tx) => {
        const info = transactionLabel(tx.type, tx.reason);
        return <span>{info.icon} {info.label}</span>;
      },
    },
    {
      title: '수량', dataIndex: 'amount', width: 80, align: 'right',
      render: (v, tx) => {
        const isPos = tx.type === 'EARN' || tx.type === 'DEPOSIT' || tx.type === 'FAMILY_TRANSFER_IN';
        return <Text type={isPos ? 'success' : 'danger'} strong>{isPos ? '+' : '−'}{Math.abs(v)}</Text>;
      },
    },
    { title: '잔여', dataIndex: 'balance_after', width: 80, align: 'right', render: (v) => `${v}개` },
  ];

  return (
    <div className="zb-desk">
      <div className="zb-overview">
        <aside className="zb-family-column"><span className="zb-caption">가족 선택</span>
        <div className="zb-family-search">{nextMemberAction}</div>
        <div className="zb-family" aria-label="거래할 가족 선택">
          {targets.map(m => <button className="zb-person" type="button" key={m.member_id} aria-pressed={m.member_id === activeMemberId}
            disabled={processing} onClick={() => setActiveMemberId(m.member_id)} data-testid={`bank-family-${m.member_id}`}>
            <span className="zb-person-copy"><strong>{getDisplayName(m)}</strong></span>
            <small className="zb-family-role">{m.member_type === 'CHILD' ? '자녀' : '회원'}</small>
          </button>)}
        </div>

        </aside>
        <section className="zb-member-dashboard">
          <div className="zb-member-balance">
            <div><span className="zb-caption">{getDisplayName(activeMember)}님의 보유 주화</span>
              <strong data-testid="bank-wallet-balance">{activeBalance.toLocaleString()}<small>개</small></strong>
            </div>
            <img src={bankCoin} alt="" />
            <div className="zb-balance-tools">
              <button type="button" onClick={openHistory} disabled={processing} data-testid="btn-bank-history"><HistoryOutlined /> 주화 내역</button>
              <button type="button" onClick={() => refresh?.()} disabled={processing} aria-label="잔액 새로고침"><ReloadOutlined /></button>
            </div>
          </div>
          <div className="zb-action-cards">
            {operations.map(item => <button type="button" className="zb-action-card" key={item.key} disabled={processing}
              onClick={() => { setActiveTab(item.key); resetInputs(); setOperationOpen(true); }} data-testid={`bank-operation-${item.key}`}>
              <span className="zb-action-art" aria-hidden="true">
                <img src={item.key === 'exchange' ? withdrawCharacter : item.key === 'deposit' ? depositCharacter : donateCharacter} alt="" draggable="false" />
              </span>
              <strong>{item.title}</strong>
            </button>)}
          </div>
        </section>
      </div>
      <Modal open={operationOpen} onCancel={() => { if (!processing) setOperationOpen(false); }} footer={null} centered width={1020}
        maskClosable={!processing} keyboard={!processing} closable={!processing} destroyOnHidden title={null} aria-label="주화 수량 입력"
        rootClassName="zb-operation-root" className="zb-operation-modal" transitionName="" maskTransitionName="">
        <div className="zb-desk zb-transaction">
        <section className="zb-entry" aria-label="주화 수량 입력">
          <div className="zb-entry-head"><h2>{operation.title} 수량</h2><span>{activeTab === 'donate' ? `최대 ${MAX_DONATE}개` : activeTab === 'exchange' ? '보유 주화 내에서 입력' : '받은 실물 주화 수량'}</span></div>
          <div className="zb-quantity" aria-live="polite"><strong data-testid="bank-quantity">{amount.toLocaleString()}</strong><span>개</span>
            {activeTab === 'exchange' && <button type="button" disabled={processing || activeBalance <= 0} onClick={() => setExchangeAmount(activeBalance)} data-testid="btn-bank-exchange-full">전량 선택</button>}
          </div>
          <div className="zb-keypad">
            {['1','2','3','4','5','6','7','8','9','clear','0','backspace'].map(key => <button type="button" key={key} disabled={processing}
              onClick={() => enterQuantity(key)} data-testid={`bank-key-${key}`} aria-label={key === 'clear' ? '수량 초기화' : key === 'backspace' ? '한 자리 지우기' : key}>
              {key === 'clear' ? '초기화' : key === 'backspace' ? '⌫' : key}
            </button>)}
          </div>
        </section>
        <aside className="zb-review" aria-label="거래 확인">
          <dl>
            <div className="zb-selected-quantity"><dt>선택한 수량</dt><dd>{amount.toLocaleString()}<small>개</small></dd></div>
            <div className="zb-after"><dt>{activeTab === 'donate' ? '디지털 잔액' : '처리 후 잔액'}</dt>
              <dd>{(activeTab === 'exchange' ? activeBalance - amount : activeTab === 'deposit' ? activeBalance + amount : activeBalance).toLocaleString()}<small>개</small></dd></div>
          </dl>
          <p className="zb-review-note">{activeTab === 'donate' ? '실물 주화 기부입니다. 디지털 잔액은 차감하지 않습니다.' : activeTab === 'deposit' && hasPassport ? '여권 저축 이자는 처리 후 별도로 반영됩니다.' : activeTab === 'exchange' ? '처리 후 해당 수량의 실물 주화를 전달해 주세요.' : '실물 주화를 받은 뒤 저축해 주세요.'}</p>
          {opError && <div className="zb-error" role="alert">{opError}</div>}
          <button type="button" className="zb-submit" disabled={processing || amount <= 0 || (activeTab === 'exchange' && amount > activeBalance)} onClick={submitQuantity}
            data-testid={activeTab === 'exchange' ? 'btn-bank-exchange-partial' : activeTab === 'deposit' ? 'btn-bank-deposit' : 'btn-bank-donate'}>
            {processing ? '처리 중…' : `${amount.toLocaleString()}개 ${operation.title}하기`}
          </button>
        </aside>
      </div>
      </Modal>
      {/* 거래 결과 모달 */}
      <Modal
        open={Boolean(resultPopup)}
        footer={null}
        closable
        onCancel={() => setResultPopup(null)}
        centered
        width={560}
        className="zb-result-modal"
        rootClassName="zb-operation-root"
        aria-label="주화 처리 완료"
        destroyOnHidden
        transitionName=""
        maskTransitionName=""
      >
        {resultPopup && (
          <section className="zb-result" data-testid="bank-result">
            <span className="zb-result-mark" aria-hidden="true"><CheckOutlined /></span>
            <h2>{resultPopup.type === 'exchange' ? '인출' : resultPopup.type === 'deposit' ? '저축' : '기부'} 완료!</h2>
            <dl className="zb-result-details">
              <div><dt>대상자</dt><dd>{resultPopup.ownerName}</dd></div>
              <div><dt>처리 수량</dt><dd>{resultPopup.amount.toLocaleString()}<small>개</small></dd></div>
              {resultPopup.type === 'deposit' && resultPopup.interest > 0 && <div><dt>적립 이자</dt><dd>+{resultPopup.interest.toLocaleString()}<small>개</small></dd></div>}
            </dl>
            <div className="zb-result-balance">
              <span>현재 잔액</span>
              <strong>{resultPopup.balance != null ? <>{resultPopup.balance.toLocaleString()}<small>개</small></> : '확인 필요'}</strong>
            </div>
            <Button type="primary" className="zb-result-confirm" block onClick={() => setResultPopup(null)}>확인</Button>
          </section>
        )}
      </Modal>

      {/* 거래내역 Drawer */}
      <Drawer
        open={historyDrawer.open}
        title={`${getDisplayName(activeMember)} 주화 이력 (잔액 ${activeBalance.toLocaleString()}개)`}
        placement="bottom"
        height="65%"
        onClose={() => setHistoryDrawer({ ...historyDrawer, open: false })}
      >
        {historyDrawer.loading && <Spin />}
        {historyDrawer.error && <Alert type="error" showIcon message={historyDrawer.error} />}
        {!historyDrawer.loading && !historyDrawer.error && (
          historyDrawer.txs.length === 0
            ? <Empty description="주화 이력이 없습니다" />
            : (
              <Table
                size="small"
                rowKey={(r) => r.transaction_id || `${r.created_at}-${r.amount}-${r.type}`}
                columns={historyColumns}
                dataSource={historyDrawer.txs}
                pagination={false}
                scroll={{ y: 'calc(60vh - 120px)' }}
              />
            )
        )}
      </Drawer>
    </div>
  );
}
